import React, { useState } from "react";
import { Alert, Avatar, Badge, Button, PageHeader, SwitchHorizontal01 } from "@opendate-app/setlist";
import { DetailColumns } from "../ClassesShared/DetailPageLayout";
import TintedAlert from "../ClassesShared/TintedAlert";
import initialsFor from "../ClassesShared/initials";
import personColorProps from "../ClassesShared/personColor";
import "../ClassesShared/classesShared.css";
import { registrationStatusBadge } from "./constants/registrationStatus";
import TransferRegistrationModal from "./TransferRegistrationModal";
import CancelRegistrationModal from "./registrationPage/CancelRegistrationModal";
import ClassCard from "./registrationPage/ClassCard";
import CommunicationsCard from "./registrationPage/CommunicationsCard";
import LearnerDetailsCard from "./registrationPage/LearnerDetailsCard";
import OrderPaymentCard from "./registrationPage/OrderPaymentCard";
import SessionsCard from "./registrationPage/SessionsCard";
import { formatDateTime } from "../ClassesShared/classLabels";
import { useRegistrationPageContext } from "./registrationPage/RegistrationPageContext";
import ClassesLoading from "../ClassesShared/ClassesLoading";

export default function RegistrationDetailPage() {
  const { registration, loadError, paths, csrfToken, canManage, onTransferred } = useRegistrationPageContext();
  const [confirming, setConfirming] = useState(false);
  const [transferring, setTransferring] = useState(false);

  if (loadError) return <Alert color="error" role="alert" description="Could not load this registration." />;
  if (!registration) return <ClassesLoading />;

  const { dashboard } = registration;
  const canCancel = canManage && registration.can_cancel;
  const canTransfer = canManage && registration.transferable;
  const { refundable } = registration;
  const statusBadge = registrationStatusBadge(registration);

  return (
    <div data-testid="registration-detail-page">
      <div className="mb-4">
        <PageHeader
          divider={false}
          avatar={<Avatar size="xl" initials={initialsFor(registration.full_name)} {...personColorProps(registration.full_name)} />}
          title={(
            <span className="d-inline-flex align-items-center flex-wrap classes-gap-8">
              <span role="heading" aria-level={1}>{registration.full_name}</span>
              <Badge color={statusBadge.color} dot>{statusBadge.label}</Badge>
              {registration.payment_kind === "comp" && <Badge color="blue" dot>Comp</Badge>}
            </span>
          )}
          supportingText={[
            dashboard.class_info.name,
            `Registered ${formatDateTime(dashboard.registered_at, dashboard.time_zone)}`,
            `Order #${registration.order_number}`,
          ].join(" · ")}
          actions={(canCancel || canTransfer) && (
            <div className="d-flex flex-wrap classes-gap-8">
              {canTransfer && <Button variant="secondary" icon={SwitchHorizontal01} onClick={() => setTransferring(true)}>Transfer</Button>}
              {canCancel && (
                <span className="classes-danger-outline">
                  <Button variant="secondary" onClick={() => setConfirming(true)}>{refundable ? "Cancel & Refund" : "Cancel Registration"}</Button>
                </span>
              )}
            </div>
          )}
        />
      </div>
      <h2 className="sr-only">Registration details</h2>

      {registration.refund_pending && (
        <div className="mb-3">
          <TintedAlert color="brand" title="Refund processing" description="The registration cancels and the spot opens once the refund finishes." />
        </div>
      )}

      <DetailColumns rail={<><ClassCard /><CommunicationsCard /></>}>
        <LearnerDetailsCard />
        <OrderPaymentCard />
        <SessionsCard />
      </DetailColumns>

      {confirming && <CancelRegistrationModal onClose={() => setConfirming(false)} />}
      {transferring && (
        <TransferRegistrationModal
          registration={registration}
          path={paths.registration}
          csrfToken={csrfToken}
          onClose={() => setTransferring(false)}
          onTransferred={(data) => { setTransferring(false); onTransferred(data); }}
        />
      )}
    </div>
  );
}
